import React, { memo } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';

const CenteredContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'gradientStart' &&
    prop !== 'gradientEnd' &&
    prop !== 'gradientDirection' &&
    prop !== 'isEnclosedMode' &&
    prop !== 'promptContainer',
})(
  ({
    theme,
    gradientStart,
    gradientEnd,
    gradientDirection = '45deg',
    isEnclosedMode,
    promptContainer,
  }) => {
    return {
      display: 'grid',
      minHeight: '100%',
      placeItems: 'center',
      width: '100%',
      background:
        !isEnclosedMode || promptContainer
          ? `linear-gradient(${gradientDirection}, ${gradientStart} 60%, ${gradientEnd} 100%)`
          : theme.palette.background.default,
    };
  }
);

/**
 * GradientContainer component renders a full-viewport centered container with a gradient background.
 * On mobile devices, it renders children directly without the gradient container.
 * On larger screens, it applies a gradient background and centers the content.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} [props.children] - The content to be rendered inside the container
 * @param {string} [props.id] - Optional ID attribute for the container element
 * @param {string} props.gradientStart - Starting color for the gradient (CSS color value)
 * @param {string} props.gradientEnd - Ending color for the gradient (CSS color value)
 * @param {string} [props.gradientDirection='45deg'] - Direction of the gradient (CSS angle value)
 * @param {boolean} [props.isEnclosedMode] - When true, uses block display instead of grid for layout
 * @returns {JSX.Element} The rendered container with gradient background or children directly on mobile
 */
function GradientContainer({
  children,
  gradientStart,
  gradientEnd,
  gradientDirection,
  id,
  isEnclosedMode,
  promptContainer = false,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('mobile'));
  if (isMobile) {
    return <>{children}</>;
  }
  // If the screen is not mobile, render the centered container with gradient
  return (
    <CenteredContainer
      id={id}
      gradientStart={gradientStart}
      gradientEnd={gradientEnd}
      gradientDirection={gradientDirection}
      isEnclosedMode={isEnclosedMode}
      promptContainer={promptContainer}
    >
      {children}
    </CenteredContainer>
  );
}

GradientContainer.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
  gradientStart: PropTypes.string.isRequired,
  gradientEnd: PropTypes.string.isRequired,
  gradientDirection: PropTypes.string,
  isEnclosedMode: PropTypes.bool,
  promptContainer: PropTypes.bool,
};

export default memo(GradientContainer);
